import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import axios from "axios";
import config from "../../config";
import { getYearlyPlan } from "../../actions/postAction";
import { getVisionTodoSuggestions } from "../../actions/aiAction";
import {
  fetchTodos,
  createTodo as createTodoApi,
  createTodoBatch,
  updateTodo as updateTodoApi,
  deleteTodo as deleteTodoApi,
} from "../../actions/todoAction";
import { toast } from "react-toastify";
import "./NoteDetails.css";
import "./VisionTodos.css";

const MS_IN_DAY = 1000 * 60 * 60 * 24;
const MAX_ACTIVE = 15;

const COLORS = [
  { id: "none",   label: "No tag",  hex: null },
  { id: "blue",   label: "Focus",   hex: "#6366f1" },
  { id: "orange", label: "Push",    hex: "#f17501" },
  { id: "green",  label: "Quick",   hex: "#22c55e" },
  { id: "pink",   label: "Maybe",   hex: "#e0416d" },
  { id: "purple", label: "Idea",    hex: "#a855f7" },
];


const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const dateBucket = (dueISO) => {
  if (!dueISO) return "later";
  const today = startOfDay(new Date());
  const due = startOfDay(new Date(dueISO));
  const diff = Math.round((due - today) / MS_IN_DAY);
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff <= 7) return "thisWeek";
  return "later";
};

const BUCKET_META = {
  overdue:  { label: "Overdue",     order: 0, tone: "danger" },
  today:    { label: "Today",       order: 1, tone: "primary" },
  tomorrow: { label: "Tomorrow",    order: 2, tone: "warm" },
  thisWeek: { label: "This week",   order: 3, tone: "neutral" },
  later:    { label: "Later",       order: 4, tone: "neutral" },
};

const formatDueLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};


const DUE_PRESETS = [
  { label: "Today",    days: 0 },
  { label: "Tomorrow", days: 1 },
];

const isoFromDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isDuePreset = (due, days) => due === isoFromDays(days);

// Server docs (from .lean()) have _id but no id virtual — normalize on entry
const normalizeTodo = (t) => ({ ...t, id: t.id ?? t._id?.toString() ?? t._id });

const VisionTodos = () => {
  const { id: planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [plan, setPlan]               = useState(null);
  const [planRecords, setPlanRecords] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [todos, setTodos]             = useState([]);
  const [text, setText]               = useState("");
  const [due, setDue]                 = useState("");
  const [colorId, setColorId]         = useState("none");
  const [recordIdForNew, setRecordIdForNew] = useState("");
  const [recordPickerCtx, setRecordPickerCtx] = useState(null); // "new" | todoId | "ai_${i}"
  const [updateOpenFor, setUpdateOpenFor]   = useState(null); // todo id
  const [updateText, setUpdateText]         = useState("");
  const [updatePosting, setUpdatePosting]   = useState(false);
  const [filter, setFilter]           = useState("active"); // all | active | done
  const [showAi, setShowAi]               = useState(false);
  const [aiLoading, setAiLoading]         = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSelected, setAiSelected]       = useState(new Set());
  const inputRef       = useRef(null);
  const dateInputRef   = useRef(null);
  const textDebounce   = useRef({});

  // Clean up debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(textDebounce.current).forEach(clearTimeout);
    };
  }, []);

  // Load plan, records, and todos
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [p, fetchedTodos] = await Promise.all([
        dispatch(getYearlyPlan(planId)),
        fetchTodos(planId).catch(() => []),
      ]);
      if (!active) return;
      if (p) {
        setPlan(p);
        const records = (p.posts || []).map((entry) => entry.postId).filter(Boolean);
        setPlanRecords(records);
      }
      setTodos(fetchedTodos.map(normalizeTodo));
      setLoading(false);
    })();
    return () => { active = false; };
  }, [planId, dispatch]);

  const addTodo = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!due) {
      toast.error("Pick a due date before adding.");
      return;
    }
    if (activeCount >= MAX_ACTIVE) {
      toast.error("Active todo limit reached. Mark some as done to add more.");
      return;
    }
    setText("");
    setDue("");
    setColorId("none");
    setRecordIdForNew("");
    inputRef.current?.focus();
    try {
      const created = await createTodoApi(planId, {
        text: trimmed,
        due: due || null,
        colorId,
        recordId: recordIdForNew || null,
      });
      setTodos((prev) => [...prev, normalizeTodo(created)]);
    } catch {
      toast.error("Couldn't add todo. Try again.");
    }
  };

  const setTodoRecord = (id, recordId) => {
    const prev = todos.find((t) => t.id === id);
    setTodos((list) => list.map((t) => (t.id === id ? { ...t, recordId: recordId || null } : t)));
    updateTodoApi(planId, id, { recordId: recordId || "" }).catch(() => {
      setTodos((list) => list.map((t) => (t.id === id ? { ...t, recordId: prev?.recordId ?? null } : t)));
      toast.error("Couldn't save record link.");
    });
  };

  const archiveTodo = (id) => {
    const archivedAt = new Date().toISOString();
    setTodos((list) => list.map((t) => t.id === id ? { ...t, archivedAt } : t));
    updateTodoApi(planId, id, { archivedAt }).catch(() => {
      setTodos((list) => list.map((t) => t.id === id ? { ...t, archivedAt: null } : t));
      toast.error("Couldn't move to history.");
    });
  };

  const postUpdateToRecord = async (todo) => {
    if (!todo.recordId || !updateText.trim()) return;
    setUpdatePosting(true);
    try {
      await axios.post(
        `${config.apiUrl}/api/v1/addupdate`,
        { postId: todo.recordId, updateNote: updateText.trim() },
        { withCredentials: true }
      );
      toast.success("Update added to record");
      setUpdateOpenFor(null);
      setUpdateText("");
      archiveTodo(todo.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't post update. Try again.");
    } finally {
      setUpdatePosting(false);
    }
  };

  const findRecord = (id) => planRecords.find((r) => r._id === id) || null;

  const pickRecord = (recordId) => {
    if (recordPickerCtx === "new") {
      setRecordIdForNew(recordId);
    } else if (typeof recordPickerCtx === "string" && recordPickerCtx.startsWith("ai_")) {
      const idx = parseInt(recordPickerCtx.slice(3), 10);
      updateAiField(idx, "recordId", recordId);
    } else if (recordPickerCtx) {
      setTodoRecord(recordPickerCtx, recordId);
    }
    setRecordPickerCtx(null);
  };

  const recordLabel = (r) =>
    r.category === "Thoughts" && !(r.note || "").trim()
      ? "Thoughts"
      : (r.note || "untitled");

  const toggleDone = (id) => {
    const prev = todos.find((t) => t.id === id);
    const done = !prev?.done;
    const completedAt = done ? new Date().toISOString() : null;
    setTodos((list) => list.map((t) => t.id === id ? { ...t, done, completedAt } : t));
    updateTodoApi(planId, id, { done, completedAt }).catch(() => {
      setTodos((list) => list.map((t) => t.id === id ? { ...t, done: !done, completedAt: prev?.completedAt ?? null } : t));
      toast.error("Couldn't save. Try again.");
    });
  };

  const removeTodo = (id) => {
    const snapshot = todos.find((t) => t.id === id);
    setTodos((list) => list.filter((t) => t.id !== id));
    deleteTodoApi(planId, id).catch(() => {
      if (snapshot) setTodos((list) => [...list, snapshot]);
      toast.error("Couldn't delete. Try again.");
    });
  };

  const updateTodoText = (id, value) => {
    setTodos((list) => list.map((t) => (t.id === id ? { ...t, text: value } : t)));
    clearTimeout(textDebounce.current[id]);
    textDebounce.current[id] = setTimeout(() => {
      updateTodoApi(planId, id, { text: value }).catch(() => {
        toast.error("Couldn't save text. Try again.");
      });
    }, 800);
  };

  const setTodoColor = (id, cid) => {
    const prev = todos.find((t) => t.id === id);
    setTodos((list) => list.map((t) => (t.id === id ? { ...t, colorId: cid } : t)));
    updateTodoApi(planId, id, { colorId: cid }).catch(() => {
      setTodos((list) => list.map((t) => t.id === id ? { ...t, colorId: prev?.colorId ?? "none" } : t));
    });
  };

  const activeCount = useMemo(() => todos.filter((t) => !t.done && !t.archivedAt).length, [todos]);

  const visible = useMemo(() => {
    if (filter === "active")  return todos.filter((t) => !t.done && !t.archivedAt);
    if (filter === "done")    return todos.filter((t) => t.done && !t.archivedAt);
    if (filter === "history") return [...todos.filter((t) => t.archivedAt)].sort((a, b) => new Date(b.archivedAt) - new Date(a.archivedAt));
    return todos;
  }, [todos, filter]);

  const grouped = useMemo(() => {
    if (filter === "history") return [];
    const map = { overdue: [], today: [], tomorrow: [], thisWeek: [], later: [] };
    visible.forEach((t) => {
      if (t.done) { map.later.push(t); return; }
      map[dateBucket(t.due)].push(t);
    });
    return Object.entries(map)
      .filter(([_, list]) => list.length > 0)
      .sort(([a], [b]) => BUCKET_META[a].order - BUCKET_META[b].order);
  }, [visible, filter]);

  const stats = useMemo(() => {
    const active  = todos.filter((t) => !t.done && !t.archivedAt).length;
    const done    = todos.filter((t) => t.done && !t.archivedAt).length;
    const history = todos.filter((t) => t.archivedAt).length;
    return { active, done, history, total: todos.length };
  }, [todos]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      addTodo();
    }
  };

  const dueToDateISO = (dueWindow) => {
    const d = new Date();
    if (dueWindow === "today")    return d.toISOString().slice(0, 10);
    if (dueWindow === "tomorrow") { d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }
    if (dueWindow === "thisWeek") { d.setDate(d.getDate() + 5); return d.toISOString().slice(0, 10); }
    return null;
  };

  const openAiPanel = async () => {
    setShowAi(true);
    if (aiSuggestions.length > 0) return;
    await generateAiSuggestions();
  };

  const generateAiSuggestions = async () => {
    setAiLoading(true);
    try {
      const activeTodos = todos
        .filter((t) => !t.done && !t.archivedAt)
        .map((t) => t.text.trim())
        .filter(Boolean);
      const data = await getVisionTodoSuggestions(planId, activeTodos);
      const list = (data?.todos || []).map((t, i) => ({
        ...t,
        _key: `ai_${Date.now()}_${i}`,
      }));
      setAiSuggestions(list);
      setAiSelected(new Set(list.map((_, i) => i)));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't generate todos. Try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const updateAiField = (i, field, value) => {
    setAiSuggestions((prev) => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  };

  const toggleAiSelect = (i) => {
    setAiSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const addSelectedAiTodos = async () => {
    const picked = aiSuggestions
      .filter((_, i) => aiSelected.has(i))
      .filter((s) => (s.text || "").trim());
    if (picked.length === 0) return;
    if (activeCount >= MAX_ACTIVE) {
      toast.error("Active todo limit reached. Mark some as done to add more.");
      return;
    }
    const slots = MAX_ACTIVE - activeCount;
    const trimmedPicked = picked.slice(0, slots);
    if (trimmedPicked.length < picked.length) {
      toast.info(`Only ${slots} slot${slots === 1 ? "" : "s"} left. Added first ${slots}.`);
    }
    setShowAi(false);
    setAiSuggestions([]);
    setAiSelected(new Set());
    try {
      const created = await createTodoBatch(planId, trimmedPicked.map((s) => ({
        text: s.text.trim(),
        due: dueToDateISO(s.due),
        colorId: s.colorId || "none",
        recordId: s.recordId || null,
      })));
      setTodos((prev) => [...prev, ...created.map(normalizeTodo)]);
      toast.success(`${created.length} todo${created.length === 1 ? "" : "s"} added`);
    } catch {
      toast.error("Couldn't add todos. Try again.");
    }
  };

  return (
    <div className={`nd-outer ${planRecords.length > 0 ? "nd-outer--with-sidebar" : ""}`}>
      {planRecords.length > 0 && (
        <>
          <button
            type="button"
            className="nd-sidebar-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle records list"
          >
            ☰
          </button>

          {sidebarOpen && (
            <div className="nd-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
          )}

          <aside className={`nd-sidebar ${sidebarOpen ? "nd-sidebar--open" : ""}`}>
            <div className="nd-sidebar-vision">
              <button
                type="button"
                className="nd-sidebar-vision-name"
                onClick={() => navigate(`/dashboard/year-plan/${planId}`)}
                title="Open Vision"
              >
                {plan?.name || plan?.heading || "Vision"}
              </button>
              <div className="nd-sidebar-actions">
                <button
                  type="button"
                  className="nd-sidebar-action"
                  onClick={() => navigate(`/dashboard/start/vision-setup/${planId}`)}
                >
                  Add Record
                </button>
                <button
                  type="button"
                  className="nd-sidebar-action"
                  onClick={() => navigate(`/dashboard/start/vision-setup/${planId}`)}
                  title="Edit Vision"
                >
                  <i className="bx bx-edit-alt" /> Edit
                </button>
                <button
                  type="button"
                  className="nd-sidebar-action active"
                  title="Vision Todos"
                >
                  <i className="bx bx-check-square" /> Todos
                </button>
                <button
                  type="button"
                  className="nd-sidebar-action"
                  onClick={() => navigate(`/dashboard/year-plan/${planId}/analysis`)}
                  title="Vision Report"
                >
                  <i className="bx bx-bar-chart-alt-2" /> Report
                </button>
              </div>
            </div>
            <div className="nd-sidebar-head">
              <span className="nd-sidebar-title">Records in this Vision</span>
              <button
                type="button"
                className="nd-sidebar-close"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ul className="nd-sidebar-list">
              {planRecords.map((r) => {
                const isThoughts = r.category === "Thoughts";
                const empty = !r.note || !r.note.trim();
                const preview = isThoughts && empty
                  ? "your thoughts thread…"
                  : (r.note || "").slice(0, 90) + ((r.note || "").length > 90 ? "…" : "");
                return (
                  <li key={r._id}>
                    <button
                      type="button"
                      className={`nd-sidebar-item${isThoughts && empty ? " thoughts" : ""}`}
                      onClick={() => {
                        setSidebarOpen(false);
                        navigate(`/dashboard/note-details/${r._id}`);
                      }}
                    >
                      <span className={`nd-sidebar-dot ${r.status || "active"}`} />
                      <span className="nd-sidebar-item-text">{preview || "untitled"}</span>
                      {r.category && (
                        <span className="nd-sidebar-item-cat">{r.category}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        </>
      )}

    <div className="vt-page">
      <Helmet>
        <title>{`Todos · ${plan?.name || "Vision"} | dmyor`}</title>
      </Helmet>

      <div className="vt-bg" aria-hidden="true">
        <div className="vt-bg-orb vt-bg-orb-a" />
        <div className="vt-bg-orb vt-bg-orb-b" />
      </div>

      <header className="vt-header">
        <div className="vt-header-titles">
          <h1 className="vt-title">{loading ? "…" : (plan?.heading || plan?.name || "Your Vision")}</h1>
        </div>

        <div className="vt-stats">
          <div className="vt-stat">
            <span className="vt-stat-num">{stats.active}</span>
            <span className="vt-stat-label">Active</span>
          </div>
          <div className="vt-stat-divider" />
          <div className="vt-stat">
            <span className="vt-stat-num">{stats.done}</span>
            <span className="vt-stat-label">Done</span>
          </div>
        </div>
      </header>

      {/* Quick add */}
      <section className="vt-quick-add">
        <div className="vt-input-wrap">
          <input
            ref={inputRef}
            type="text"
            className="vt-input"
            placeholder="What needs to happen for this vision today?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKey}
          />
          <button
            type="button"
            className="vt-add-btn"
            onClick={addTodo}
            disabled={!text.trim() || !due || activeCount >= MAX_ACTIVE}
            aria-label="Add todo"
          >
            +
          </button>
        </div>
        {activeCount >= MAX_ACTIVE && (
          <p className="vt-limit-hint">Active todo limit reached ({MAX_ACTIVE}). Mark some as done to add more.</p>
        )}

        <div className="vt-controls">
          <div className="vt-due-row">
            <span className="vt-control-label">Due</span>
            <div className="vt-due-pills">
              {DUE_PRESETS.map((p) => {
                const isActive = isDuePreset(due, p.days);
                return (
                  <button
                    key={p.label}
                    type="button"
                    className={`vt-due-pill${isActive ? " active" : ""}`}
                    onClick={() => setDue(isActive ? "" : isoFromDays(p.days))}
                  >
                    {p.label}
                  </button>
                );
              })}
              <button
                type="button"
                className={`vt-due-pill vt-due-pill--custom${due && !DUE_PRESETS.some((p) => isDuePreset(due, p.days)) ? " active" : ""}`}
                onClick={() => {
                  if (dateInputRef.current?.showPicker) dateInputRef.current.showPicker();
                  else dateInputRef.current?.click();
                }}
              >
                {due && !DUE_PRESETS.some((p) => isDuePreset(due, p.days))
                  ? formatDueLabel(due)
                  : "Pick date"}
              </button>
              <input
                ref={dateInputRef}
                type="date"
                className="vt-due-hidden"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
              {due && (
                <button
                  type="button"
                  className="vt-due-clear"
                  onClick={() => setDue("")}
                  aria-label="Clear due date"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="vt-color-row">
            {COLORS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`vt-color-dot${colorId === c.id ? " active" : ""}`}
                style={{
                  background: c.hex || "transparent",
                  borderColor: c.hex || "rgba(255,255,255,0.2)",
                }}
                onClick={() => setColorId(c.id)}
                title={c.label}
                aria-label={c.label}
              />
            ))}
          </div>

          {planRecords.length > 0 && (
            <div className="vt-control">
              <span className="vt-control-label">Link</span>
              <button
                type="button"
                className={`vt-record-pick-btn${recordIdForNew ? " has-value" : ""}`}
                onClick={() => setRecordPickerCtx("new")}
                title="Link this todo to a record"
              >
                <span className="vt-record-pick-label">
                  {recordIdForNew
                    ? recordLabel(planRecords.find((x) => x._id === recordIdForNew) || {}).slice(0, 30)
                    : "No record"}
                </span>
                <span className="vt-record-pick-arrow">↓</span>
              </button>
            </div>
          )}
        </div>
      </section>

      <div className="vt-customize-panel">
        <p className="vt-customize-title">Tags</p>
        <div className="vt-customize-tags">
          {COLORS.filter((c) => c.hex).map((c) => (
            <span key={c.id} className="vt-customize-tag">
              <span className="vt-customize-tag-dot" style={{ background: c.hex }} />
              {c.label}
            </span>
          ))}
        </div>
        <p className="vt-customize-hint">Pick a color when you add a todo, or click the dot on any todo to change it.</p>
      </div>

      {/* Filter pills */}
      <div className="vt-filters">
        {[
          { key: "active",  label: "Active",  count: stats.active },
          { key: "done",    label: "Done",    count: stats.done },
          { key: "history", label: "History", count: stats.history },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            type="button"
            className={`vt-filter${filter === key ? " active" : ""}`}
            onClick={() => setFilter(key)}
          >
            {label}
            {count > 0 && <span className="vt-filter-count">{count}</span>}
          </button>
        ))}

        <button
          type="button"
          className="vt-ai-btn"
          onClick={openAiPanel}
          disabled={activeCount >= MAX_ACTIVE}
          title={activeCount >= MAX_ACTIVE ? "Mark some todos as done to generate more" : "Generate todos from your records"}
        >
          ✦ Generate Todos
        </button>

      </div>

      {/* List */}
      <main className="vt-list-wrap">
        {filter === "history" ? (
          visible.length === 0 ? (
            <div className="vt-empty small">
              <p className="vt-empty-title">No history yet.</p>
              <p className="vt-empty-sub">Todos move here when you post an update to their linked record, or manually send them to history.</p>
            </div>
          ) : (
            <section className="vt-section tone-neutral">
              <header className="vt-section-head">
                <h2 className="vt-section-title">History</h2>
                <span className="vt-section-count">{visible.length}</span>
              </header>
              <ul className="vt-list">
                {visible.map((t) => {
                  const color = COLORS.find((c) => c.id === t.colorId);
                  const linkedRecord = findRecord(t.recordId);
                  const linkedLabel = linkedRecord
                    ? (() => {
                        if (linkedRecord.category === "Thoughts" && !(linkedRecord.note || "").trim()) return "Thoughts";
                        const n = linkedRecord.note || "untitled";
                        return n.length > 50 ? n.slice(0, 50) + "…" : n;
                      })()
                    : null;
                  return (
                    <li
                      key={t.id}
                      className="vt-item done vt-item--history"
                      style={color?.hex ? { borderLeftColor: color.hex } : undefined}
                    >
                      <div className="vt-item-row">
                        <div className="vt-check checked">
                          <span className="vt-check-mark">✓</span>
                        </div>
                        <div className="vt-item-main">
                          <span className="vt-item-text vt-item-text--static">{t.text}</span>
                          {linkedLabel && (
                            <span className="vt-link-chip" title="Linked record">
                              <span className="vt-link-icon">↳</span>
                              {linkedLabel}
                            </span>
                          )}
                        </div>
                        <div className="vt-item-meta">
                          <span className="vt-history-date">
                            {formatDueLabel(t.createdAt)}
                          </span>
                          <button
                            type="button"
                            className="vt-delete"
                            onClick={() => removeTodo(t.id)}
                            aria-label="Delete"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )
        ) : todos.filter((t) => !t.archivedAt).length === 0 && filter === "active" ? (
          <div className="vt-empty">
            <div className="vt-empty-orb" aria-hidden="true" />
            <p className="vt-empty-title">No todos yet for this vision.</p>
            <p className="vt-empty-sub">Drop the first thing that needs to happen above. Daily steps, scrappy ideas, whatever moves you forward.</p>
          </div>
        ) : grouped.length === 0 ? (
          <div className="vt-empty small">
            <p className="vt-empty-title">Nothing here right now.</p>
            <p className="vt-empty-sub">Try another filter.</p>
          </div>
        ) : (
          grouped.map(([bucket, list]) => (
            <section key={bucket} className={`vt-section tone-${BUCKET_META[bucket].tone}`}>
              <header className="vt-section-head">
                <h2 className="vt-section-title">{BUCKET_META[bucket].label}</h2>
                <span className="vt-section-count">{list.length}</span>
              </header>
              <ul className="vt-list">
                {list.map((t) => {
                  const color = COLORS.find((c) => c.id === t.colorId);
                  const linkedRecord = findRecord(t.recordId);
                  const linkedLabel = linkedRecord
                    ? (() => {
                        if (linkedRecord.category === "Thoughts" && !(linkedRecord.note || "").trim()) return "Thoughts";
                        const n = linkedRecord.note || "untitled";
                        return n.length > 50 ? n.slice(0, 50) + "…" : n;
                      })()
                    : null;
                  const composerOpen = updateOpenFor === t.id;
                  return (
                    <li
                      key={t.id}
                      className={`vt-item${t.done ? " done" : ""}`}
                      style={color?.hex ? { borderLeftColor: color.hex } : undefined}
                    >
                      <div className="vt-item-row">
                        <button
                          type="button"
                          className={`vt-check${t.done ? " checked" : ""}`}
                          onClick={() => toggleDone(t.id)}
                          aria-label={t.done ? "Mark as not done" : "Mark as done"}
                        >
                          {t.done && <span className="vt-check-mark">✓</span>}
                        </button>

                        <div className="vt-item-main">
                          <textarea
                            className="vt-item-text"
                            value={t.text}
                            rows={1}
                            onChange={(e) => {
                              updateTodoText(t.id, e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                            onFocus={(e) => {
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                          />
                          {linkedLabel && (
                            <span className="vt-link-chip" title="Linked to this record">
                              <span className="vt-link-icon">↳</span>
                              {linkedLabel}
                            </span>
                          )}
                        </div>

                        <div className="vt-item-meta">
                          {t.due && !t.done && (
                            <span className={`vt-due ${bucket}`}>
                              {formatDueLabel(t.due)}
                            </span>
                          )}

                          {planRecords.length > 0 && (
                            <button
                              type="button"
                              className="vt-item-link-btn"
                              onClick={() => setRecordPickerCtx(t.id)}
                              title={t.recordId ? "Change linked record" : "Link to a record"}
                            >
                              Link record
                            </button>
                          )}

                          <div className="vt-item-colors">
                            {COLORS.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                className={`vt-color-mini${t.colorId === c.id ? " active" : ""}`}
                                style={{
                                  background: c.hex || "transparent",
                                  borderColor: c.hex || "rgba(255,255,255,0.15)",
                                }}
                                onClick={() => setTodoColor(t.id, c.id)}
                                title={c.label}
                                aria-label={c.label}
                              />
                            ))}
                          </div>

                          {t.done && t.recordId && (
                            <button
                              type="button"
                              className="vt-update-btn"
                              onClick={() => {
                                setUpdateOpenFor(composerOpen ? null : t.id);
                                setUpdateText("");
                              }}
                            >
                              {composerOpen ? "Cancel" : "+ Update record"}
                            </button>
                          )}

                          {t.done && (
                            <button
                              type="button"
                              className="vt-archive-btn"
                              onClick={() => archiveTodo(t.id)}
                              title="Move to History"
                            >
                              → History
                            </button>
                          )}

                          <button
                            type="button"
                            className="vt-delete"
                            onClick={() => removeTodo(t.id)}
                            aria-label="Delete todo"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {t.done && t.recordId && composerOpen && (
                        <div className="vt-update-composer">
                          <p className="vt-update-composer-label">
                            Update for: <strong>{linkedLabel}</strong>
                          </p>
                          <textarea
                            className="vt-update-composer-input"
                            placeholder="What happened with this? Write what you did, what you learned, what's next…"
                            value={updateText}
                            onChange={(e) => setUpdateText(e.target.value)}
                            rows={3}
                            autoFocus
                          />
                          <div className="vt-update-composer-actions">
                            <button
                              type="button"
                              className="vt-update-cancel"
                              onClick={() => { setUpdateOpenFor(null); setUpdateText(""); }}
                              disabled={updatePosting}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="vt-update-post"
                              onClick={() => postUpdateToRecord(t)}
                              disabled={updatePosting || !updateText.trim()}
                            >
                              {updatePosting ? "Posting…" : "Post update"}
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </main>
    </div>

    {showAi && (
      <div className="vt-ai-overlay" onClick={() => setShowAi(false)}>
        <div className="vt-ai-panel" onClick={(e) => e.stopPropagation()}>
          <div className="vt-ai-head">
            <div>
              <p className="vt-ai-title">Todos from your records</p>
            </div>
            <button
              type="button"
              className="vt-ai-close"
              onClick={() => setShowAi(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="vt-ai-body">
            {aiLoading && (
              <div className="vt-ai-loading">
                <div className="vt-ai-spinner" />
                <p>Thinking through your records…</p>
              </div>
            )}

            {!aiLoading && aiSuggestions.length === 0 && (
              <div className="vt-ai-loading">
                <p>No suggestions yet.</p>
                <button type="button" className="vt-ai-retry" onClick={generateAiSuggestions}>
                  Generate
                </button>
              </div>
            )}

            {!aiLoading && aiSuggestions.length > 0 && (
              <>
                <p className="vt-ai-hint">Edit any todo, tweak the due date or color, then add the ones you want.</p>
                <ul className="vt-ai-list">
                  {aiSuggestions.map((s, i) => {
                    const isSel = aiSelected.has(i);
                    return (
                      <li key={s._key} className={`vt-ai-item${isSel ? " selected" : ""}`}>
                        <button
                          type="button"
                          className={`vt-check${isSel ? " checked" : ""}`}
                          onClick={() => toggleAiSelect(i)}
                          aria-label={isSel ? "Skip" : "Add"}
                        >
                          {isSel && <span className="vt-check-mark">✓</span>}
                        </button>

                        <div className="vt-ai-fields">
                          <textarea
                            className="vt-ai-text"
                            value={s.text}
                            rows={1}
                            onChange={(e) => {
                              updateAiField(i, "text", e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                          />
                          {s.recordHint && (
                            <span className="vt-ai-record-hint">{s.recordHint}</span>
                          )}
                          <div className="vt-ai-meta">
                            <select
                              className="vt-ai-select"
                              value={s.due || "thisWeek"}
                              onChange={(e) => updateAiField(i, "due", e.target.value)}
                            >
                              <option value="today">Today</option>
                              <option value="tomorrow">Tomorrow</option>
                              <option value="thisWeek">This week</option>
                              <option value="later">Later</option>
                            </select>
                            <button
                              type="button"
                              className={`vt-ai-record-pick-btn${s.recordId ? " has-value" : ""}`}
                              onClick={() => setRecordPickerCtx(`ai_${i}`)}
                              title="Link to a record"
                            >
                              {s.recordId
                                ? recordLabel(planRecords.find((x) => x._id === s.recordId) || {}).slice(0, 22) + "…"
                                : "Link record"}
                              <span className="vt-record-pick-arrow">↓</span>
                            </button>
                            <div className="vt-ai-colors">
                              {COLORS.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  className={`vt-color-mini${s.colorId === c.id ? " active" : ""}`}
                                  style={{
                                    background: c.hex || "transparent",
                                    borderColor: c.hex || "rgba(0,0,0,0.15)",
                                  }}
                                  onClick={() => updateAiField(i, "colorId", c.id)}
                                  title={c.label}
                                  aria-label={c.label}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>

          {!aiLoading && aiSuggestions.length > 0 && (
            <div className="vt-ai-foot">
              <button type="button" className="vt-ai-regen" onClick={generateAiSuggestions}>
                ↻ Regenerate
              </button>
              <button
                type="button"
                className="vt-ai-add"
                onClick={addSelectedAiTodos}
                disabled={aiSelected.size === 0}
              >
                Add {aiSelected.size} todo{aiSelected.size === 1 ? "" : "s"}
              </button>
            </div>
          )}
        </div>
      </div>
    )}

    {recordPickerCtx !== null && (() => {
      const currentPickedId =
        recordPickerCtx === "new"
          ? recordIdForNew
          : typeof recordPickerCtx === "string" && recordPickerCtx.startsWith("ai_")
          ? (aiSuggestions[parseInt(recordPickerCtx.slice(3), 10)]?.recordId || "")
          : (todos.find((t) => t.id === recordPickerCtx)?.recordId || "");
      return (
        <div className="vt-rp-overlay" onClick={() => setRecordPickerCtx(null)}>
          <div className="vt-rp-panel" onClick={(e) => e.stopPropagation()}>
            <div className="vt-rp-head">
              <p className="vt-rp-title">Link to a record</p>
              <button
                type="button"
                className="vt-ai-close"
                onClick={() => setRecordPickerCtx(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ul className="vt-rp-list">
              <li>
                <button
                  type="button"
                  className={`vt-rp-card vt-rp-none${currentPickedId === "" ? " active" : ""}`}
                  onClick={() => pickRecord("")}
                >
                  No record
                </button>
              </li>
              {planRecords.map((r) => {
                const isThoughts = r.category === "Thoughts";
                const fullText = isThoughts && !(r.note || "").trim()
                  ? "Your thoughts thread — a running log of reflections."
                  : (r.note || "untitled");
                const isActive = currentPickedId === r._id;
                return (
                  <li key={r._id}>
                    <button
                      type="button"
                      className={`vt-rp-card${isActive ? " active" : ""}`}
                      onClick={() => pickRecord(r._id)}
                    >
                      <span className={`nd-sidebar-dot ${r.status || "active"}`} />
                      <span className="vt-rp-text">{fullText}</span>
                      {r.category && (
                        <span className="vt-rp-cat">{r.category}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    })()}
    </div>
  );
};

export default VisionTodos;
